import { useCallback, useMemo } from "react"

import { format } from "date-fns"

import { useDataLayer } from "@/features/DataLayer"
import { calculateDaysToArrival } from "@/features/DataLayer/helpers"
import { usePartnerCode } from "@/features/PartnerCode"
import { useSelectedResort } from "@/hooks"
import { useDateParam } from "@/hooks/useSkipassFlow"
import getSeason from "@/utils/getSeason"

export type AddToCartProduct = {
  id: string | null
  name: string
  price: string
  category: "SkiPass" | "Ticket" | "Insurance"
  quantity: number
}

type AddToCartCompleteParams = {
  products: AddToCartProduct[]
  currencyCode: string
  numPersons: number
}

export const useAddToCartCompleteEvent = () => {
  const pushAddToCartComplete = useDataLayer("addToCartComplete")

  const { selectedResort } = useSelectedResort()
  const { partnerCode } = usePartnerCode()
  const { startDate } = useDateParam()

  const season = useMemo(() => getSeason(startDate), [startDate])

  const runAddToCartComplete = useCallback(
    ({ products, currencyCode, numPersons }: AddToCartCompleteParams) => {
      pushAddToCartComplete({
        ecommerce: {
          add: {
            products: products.map((product) => ({
              id: product.id,
              product_id: null,
              name: product.name,
              price: product.price,
              brand: selectedResort?.name ?? null,
              category: product.category,
              coupon: partnerCode,
              variant: null,
              quantity: product.quantity,
              position: "",
              dimension16: season,
              dimension17: null,
              dimension18: calculateDaysToArrival(startDate).toString(),
              dimension20: format(startDate, "yyyy-MM-dd"),
            })),
          },
          currencyCode,
        },
        transactionCurrency: currencyCode,
        numPersons: numPersons.toString(),
        AccommodationArrivalDate: "",
        AccommodationWeek: "",
        AccommodationLength: "",
        AccommodationResort: "",
        AccommodationArea: "",
        AccommodationSubArea: "",
      })
    },
    [pushAddToCartComplete, season, startDate, selectedResort, partnerCode]
  )

  return runAddToCartComplete
}
